import React, { ChangeEvent } from "react";
import { SearchBar } from "cm-component-library/lib/CMLayout/Navbar/";
import { Redirect, RouteComponentProps, withRouter } from "react-router";
import queryString from "query-string";
import { ThunkDispatch } from "redux-thunk";
import { connect } from "react-redux";
import { Search } from "cm-layout";
import { appPaths, IAppState } from "../../../Common";
import styles from "../../Styles/SearchBarContainer.module.scss";
import {
  IGetAutocompleteResultsArguments,
  AutocompleteSearchAction,
  getAutocompleteResult,
  unloadSearch
} from "../../Actions/AutocompleteSearchActions";
import { AutocompleteItem } from "../../Models/AutocompleteItem";
import RedirectingAutocompleteResults from "../Autocomplete/RedirectingAutocompleteResults";
import SearchBarContextSelector from "./SearchBarContextSelector";
import { SearchContext } from "../../Models/SearchContext";
import { SearchContextType } from "../../Constants/LayoutEnums";
import { allContext } from "../../Reducers/SearchContextReducer";

type TParams = {
  query?: string;
};

type Props = {
  setReloadFeedback?: (reload: Boolean, context?: SearchContext, query?: string) => void;
};

type StateProps = {
  items: AutocompleteItem[];
  isLoaded: boolean;
  isLoading: boolean;
  isErrored: boolean;
  errorMessage: string;
  errorCode?: number;
};

type DispatchProps = {
  getAutocompleteResult: (args: IGetAutocompleteResultsArguments) => void;
  unloadSearch: () => void;
};

type State = {
  shouldRedirect: boolean;
  redirectUrl?: string;
  searchQuery: string;
  selectedItemIndex: number;
  redirectingToItemIndex: number;
  searchContext?: SearchContext;
  lastLocation: string;
  showContextSelector: boolean;
};

function mapStateToProps(state: IAppState) {
  return {
    items: state.autocompleteSearch.items,
    isLoaded: state.autocompleteSearch.isLoaded,
    isLoading: state.autocompleteSearch.isLoading,
    isErrored: state.autocompleteSearch.isErrored,
    errorMessage: state.autocompleteSearch.errorMessage,
    errorCode: state.autocompleteSearch.errorCode
  };
}

function mapDispatchToProps(
  dispatch: ThunkDispatch<IAppState, {}, AutocompleteSearchAction>
): DispatchProps {
  return {
    getAutocompleteResult: (args: IGetAutocompleteResultsArguments) =>
      dispatch(getAutocompleteResult(args)),
    unloadSearch: () => dispatch(unloadSearch())
  };
}

class SearchBarContainer extends React.Component<
  Props & DispatchProps & StateProps & RouteComponentProps,
  State
> {
  private node: React.RefObject<HTMLInputElement>;

  constructor(props: DispatchProps & StateProps & RouteComponentProps & Props) {
    super(props);

    const params: TParams = queryString.parse(props.location.search);

    this.state = {
      shouldRedirect: false,
      searchQuery: params.query ? params.query : "",
      selectedItemIndex: -1,
      redirectingToItemIndex: -1,
      searchContext: allContext,
      lastLocation: props.location.pathname,
      showContextSelector: false
    };

    this.node = React.createRef();

    this.onSearchClick = this.onSearchClick.bind(this);
    this.onSearchQueryChange = this.onSearchQueryChange.bind(this);
    this.onSearchKeyUp = this.onSearchKeyUp.bind(this);
    this.onSearchKeyDown = this.onSearchKeyDown.bind(this);
    this.clearAutocompleteIfLoaded = this.clearAutocompleteIfLoaded.bind(this);
    this.onActiveContextChange = this.onActiveContextChange.bind(this);
    this.getPlaceholderText = this.getPlaceholderText.bind(this);
    this.onSearchFocus = this.onSearchFocus.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  componentDidUpdate() {
    if (
      this.state.shouldRedirect &&
      !!this.state.redirectUrl &&
      this.state.redirectUrl.length > 0
    ) {
      this.setState(state => {
        return { ...state, shouldRedirect: false };
      });
    }

    if (this.state.lastLocation !== this.props.location.pathname) {
      this.setState(
        (state: State) => ({
          ...state,
          lastLocation: this.props.location.pathname,
          redirectingToItemIndex: -1
        }),
        () => {
          this.props.unloadSearch();
        }
      );
    }
  }

  componentWillUnmount() {
    document.removeEventListener("mousedown", this.onDocumentClick, false);
  }

  onSearchClick() {
    this.redirectToSearchResults(this.state.searchQuery);
  }

  onSearchQueryChange(e: ChangeEvent<HTMLInputElement>) {
    const searchQuery = e.currentTarget.value;

    this.setState(
      (prevState: State) => {
        return {
          ...prevState,
          searchQuery
        };
      },

      () => {
        // only load the autocomplete if we have entered at least 3 characters
        if (this.state.searchQuery.length > 2) {
          this.loadAutocompleteSuggestions();
        } else {
          this.clearAutocompleteIfLoaded();
        }
      }
    );
  }

  onSearchKeyUp(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") {
      if (this.state.selectedItemIndex > -1) {
        // navigate directly to selected item
        this.redirectToSelectedAutocompleteItem();
      } else {
        // run a search with the current query
        this.redirectToSearchResults(this.state.searchQuery);
        // reload feedback results
        if (this.props.setReloadFeedback) {
          this.props.setReloadFeedback(true, this.state.searchContext, this.state.searchQuery);
        }
        // set searchbar to ""

        this.setState({
          searchQuery: ""
        });
      }
    } else if (e.key === "ArrowUp") {
      // up arrow key
      this.moveSelectedAutocompleteItemUp();
    } else if (e.key === "ArrowDown") {
      // down arrow key
      this.moveSelectedAutocompleteItemDown();
    }
  }

  onSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      // disable up and down arrow if there are autocomplete results
      if (this.props.items.length > 0) {
        e.preventDefault();
      }
    }
  }

  onSearchFocus() {
    if (!this.state.showContextSelector) {
      this.setState(
        (prevState: State) => ({
          showContextSelector: true
        }),
        () => {
          // attach a listener
          document.addEventListener("mousedown", this.onDocumentClick, false);
        }
      );
    }
  }

  onActiveContextChange(context: SearchContext) {
    this.setState(state => {
      return { ...state, searchContext: context };
    });
  }

  onDocumentClick(e: MouseEvent) {
    // if the context selector is showing, check if we are clicking outside of the search bar
    if (this.state.showContextSelector && this.node.current) {
      if (this.node.current.contains(e.target as Node)) {
        // click was inside the search bar
        return;
      }

      // this is a temporary fix to allow the timeline button to still be clicked while the context selector is shown
      // the delay is so the click event on the timeline button has a moment to fire (before it is moved)
      window.setTimeout(() => {
        this.setState((prevState: State) => ({ showContextSelector: false }));
      }, 100);
    }
  }

  getSearchUrl(searchQuery: string): string {
    let basePath = appPaths.search.results;

    const queryArgs: any = {
      query: searchQuery
    };

    // add in search context information
    if (this.state.searchContext) {
      queryArgs.context = this.state.searchContext.type.toString();
      queryArgs.contextItemId = this.state.searchContext.itemId;
    }

    const stringified = queryString.stringify(queryArgs);

    basePath += `?${stringified}`;

    return basePath;
  }

  getPlaceholderText(): string {
    if (this.state.searchContext) {
      switch (this.state.searchContext.type) {
        case SearchContextType.All:
          return "Search All Items";
        case SearchContextType.App:
          return `Search ${this.state.searchContext.name}`;
        case SearchContextType.Topic:
          return `Search ${this.state.searchContext.name}`;
        default:
          return "Search Unknown Context Type";
      }
    } else {
      return "Search";
    }
  }

  redirectToSelectedAutocompleteItem() {
    if (this.state.selectedItemIndex > -1) {
      this.setState(state => {
        return { ...state, redirectingToItemIndex: state.selectedItemIndex };
      });
    }
  }

  moveSelectedAutocompleteItemUp() {
    if (this.state.selectedItemIndex > -1) {
      this.setState(state => {
        return { ...state, selectedItemIndex: state.selectedItemIndex - 1 };
      });
    }
  }

  moveSelectedAutocompleteItemDown() {
    if (this.state.selectedItemIndex < this.props.items.length - 1) {
      this.setState(state => {
        return { ...state, selectedItemIndex: state.selectedItemIndex + 1 };
      });
    }
  }

  clearAutocompleteIfLoaded() {
    if (this.props.items.length > 0) {
      this.setState(state => {
        return { ...state, selectedItemIndex: -1, redirectingToItemIndex: -1 };
      });
      this.props.unloadSearch();
    }
  }

  loadAutocompleteSuggestions() {
    this.setState(state => {
      return { ...state, selectedItemIndex: -1 };
    });
    this.props.getAutocompleteResult({ ...this.props, ...this.state, maxNumToGet: 5 });
  }

  redirectToSearchResults(searchQuery: string) {
    if (searchQuery.length > 0) {
      this.clearAutocompleteIfLoaded();
      const searchUrl = this.getSearchUrl(searchQuery);
      this.setState(state => {
        return { ...state, shouldRedirect: true, redirectUrl: searchUrl };
      });
    }
  }

  render() {
    if (this.state.shouldRedirect && !!this.state.redirectUrl) {
      return <Redirect to={this.state.redirectUrl} />;
    }

    const params: TParams = queryString.parse(this.props.location.search);

    return (
      <div className={styles.FeedbackSearchBarContainer} ref={this.node}>
        <div className={styles.searchWrapper}>
          <Search
            // onClick={this.onSearchClick}
            onChange={this.onSearchQueryChange}
            onKeyUp={this.onSearchKeyUp}
            onKeyDown={this.onSearchKeyDown}
            onFocus={this.onSearchFocus}
            value={this.state.searchQuery}
            placeholder={this.getPlaceholderText()}
            className={styles.searchBar}
          />
        </div>

        <SearchBarContextSelector
          shouldShow={this.state.showContextSelector}
          onActiveSearchContextChange={this.onActiveContextChange}
        />
        <RedirectingAutocompleteResults
          items={this.props.items}
          selectedItemIndex={this.state.selectedItemIndex}
          redirectingToItemIndex={this.state.redirectingToItemIndex}
          clearAutocompleteIfLoaded={this.clearAutocompleteIfLoaded}
        />
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SearchBarContainer));
